import { IPRAttributes } from '.';

/**
 * IPR fields whose values are unioned down the parent→child chain rather than overridden.
 * A child adds to what it inherits for these (e.g. an extra certification) without having to
 * restate the inherited set. Every field NOT listed here is a scalar the child overrides outright.
 *
 * Keep in sync with the array-typed members of {@link IPRAttributes}; the `keyof IPRAttributes`
 * annotation enforces that each entry is a real key. The merge below falls back to override
 * semantics for anything missing here — so a newly added array field must be added to this list
 * to be unioned. (Deliberately annotated rather than `as const satisfies …`: the test runner's
 * ts-node/swc transpiler cannot parse the `satisfies` operator.)
 */
export const IPR_ARRAY_FIELDS: ReadonlyArray<keyof IPRAttributes> = [
  'TypeOfBusinessTarget',
  'Certifications',
  'EnergySourcesUsed',
  'Recyclability',
  'MaterialsAbsences',
  'TargetIndustry',
];

const IPR_ARRAY_FIELD_SET: ReadonlySet<string> = new Set(IPR_ARRAY_FIELDS);

function unionPreservingOrder<T>(inherited: readonly T[] | undefined, own: readonly T[] | undefined): T[] {
  const result: T[] = [];
  const seen = new Set<T>();
  for (const value of [...(inherited ?? []), ...(own ?? [])]) {
    if (!seen.has(value)) {
      seen.add(value);
      result.push(value);
    }
  }
  return result;
}

/**
 * Merge a node's own IPR attributes over the attributes it inherits from its ancestors.
 *
 * - Scalar fields: the child's own value wins when it is set; otherwise the inherited value cascades.
 *   A key present in `own` overrides even when its value is `false` — only `undefined` (absent) inherits.
 * - Array fields ({@link IPR_ARRAY_FIELDS}): the child's values are unioned with the inherited ones
 *   (inherited first, then the child's additions, de-duplicated).
 *
 * Pure and order-stable: same inputs always produce the same blob, so the value hashed into a
 * product mixin is identical whether resolved at edit-save preview time or at publish time.
 */
export function mergeIprAttributes(
  inherited: Partial<IPRAttributes>,
  own: Partial<IPRAttributes>,
): Partial<IPRAttributes> {
  const merged: Record<string, unknown> = { ...inherited };
  for (const key of Object.keys(own)) {
    const ownValue = (own as Record<string, unknown>)[key];
    if (ownValue === undefined) {
      continue;
    }
    if (IPR_ARRAY_FIELD_SET.has(key)) {
      merged[key] = unionPreservingOrder(merged[key] as readonly unknown[] | undefined, ownValue as readonly unknown[]);
    } else {
      merged[key] = ownValue;
    }
  }
  return merged as Partial<IPRAttributes>;
}

/**
 * The IPR attribute keys that a person set, for a node resolved against its ancestors — the
 * counterpart of {@link mergeIprAttributes} for provenance rather than values.
 *
 * A node's own manually-set keys are exactly the keys of its manual-edit blob, and they union down
 * the parent→child chain: a key a user set on an ancestor segment counts as manually set for every
 * leaf below it, because that is the value the leaf ends up carrying. Note this is true even for an
 * array field the leaf also contributes to automatically — `mergeIprAttributes` unions those, so a
 * partly auto-matched list counts as manually set once any level of it was chosen by a person.
 *
 * Two kinds of key in the blob are NOT manual edits, because neither contributes a value a person
 * chose:
 * - an explicitly-`undefined` key, which is an absent edit, exactly as in `mergeIprAttributes`;
 * - an empty array, which is a user *clearing* an array field. `mergeIprAttributes` unions array
 *   fields, so an empty one adds nothing to what the node resolves to — every value in the resulting
 *   list came from somewhere else. Counting it as provenance would hand a consumer a human's
 *   authority over a list that is entirely auto-matched (a cleared `TargetIndustry` on a segment
 *   would relax the DMA industry cap for a leaf whose industries the APG guessed).
 *
 * Sorted and de-duplicated, so the same node always yields the same list whatever order the keys
 * were merged in — the value goes into a product mixin's identity.
 */
export function mergeManualIprFields(
  inherited: readonly string[] | undefined,
  ownManualIprEdits: Partial<IPRAttributes> | undefined,
): string[] {
  const fields = new Set<string>(inherited ?? []);
  const own = (ownManualIprEdits ?? {}) as Record<string, unknown>;
  for (const key of Object.keys(own)) {
    const value = own[key];
    if (value === undefined || (Array.isArray(value) && value.length === 0)) {
      continue;
    }
    fields.add(key);
  }
  return Array.from(fields).sort();
}

/**
 * IPR attributes resolved against a node's ancestors, together with which of the resulting keys a
 * person set rather than the APG auto-matching them.
 *
 * The two always travel together. `ipr` alone is lossy — it fuses auto-matched and manually chosen
 * attributes into one indistinguishable blob — and a consumer that cannot tell them apart cannot
 * filter correctly: the DMA industry triggers trust a human-chosen industry list one industry
 * further than a machine guess (PLT-6385). Threading the pair means no caller can resolve the
 * values while forgetting the provenance.
 */
export interface IprWithProvenance {
  ipr: Partial<IPRAttributes>;
  manualFields: string[];
}

/**
 * What a node with no ancestors inherits: nothing, from nobody.
 *
 * A factory rather than a shared constant on purpose. `manualFields` is a mutable array, and this
 * value is handed to every root row and every cycle fallback in a traversal — one stray `push` into
 * a shared instance would leak a manual field into unrelated products' provenance, and so into their
 * mixin identity, process-wide.
 */
export function nothingInherited(): IprWithProvenance {
  return { ipr: {}, manualFields: [] };
}

/**
 * Fold a node's own attributes and manual edits under what it inherits, yielding what the node
 * resolves to — which is in turn what its own children inherit.
 */
export function mergeIprWithProvenance(
  inherited: IprWithProvenance,
  ownIpr: Partial<IPRAttributes>,
  ownManualIprEdits: Partial<IPRAttributes> | undefined,
): IprWithProvenance {
  return {
    ipr: mergeIprAttributes(inherited.ipr, ownIpr),
    manualFields: mergeManualIprFields(inherited.manualFields, ownManualIprEdits),
  };
}
